/**
 * Control de calidad y supervisión (§9.4).
 *
 * Tres funciones, en el orden en que se usan a diario:
 *  1. Revisar las alertas automáticas y resolverlas.
 *  2. Operar la cola de verificación telefónica y presencial.
 *  3. Decidir anulaciones, que exigen la aprobación de un segundo usuario.
 *
 * Y una función de cierre: purgar los datos de contacto cuando la auditoría
 * termina, como obliga §11.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../api/client';
import { useAdmin } from '../store';
import { BarChart, StatusChip, Tile, fmtDecimal, fmtInt, fmtPct } from '../components/charts';

type Tab = 'alertas' | 'verificacion' | 'anulaciones' | 'datos';

export function QualityPage() {
  const { can, operator } = useAdmin();
  const [tab, setTab] = useState<Tab>('alertas');

  const tabs: { id: Tab; label: string; visible: boolean }[] = [
    { id: 'alertas', label: 'Alertas automáticas', visible: can('quality:read') },
    { id: 'verificacion', label: 'Verificación', visible: can('quality:verify') },
    { id: 'anulaciones', label: 'Anulaciones', visible: can('quality:read') },
    { id: 'datos', label: 'Datos de contacto', visible: can('pii:read') },
  ];

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Calidad y supervisión</h1>
        <p className="page-sub">
          §9.4: GPS obligatorio, duración mínima de 10 minutos, supervisión telefónica del 20% a las
          24–48 h y presencial del 10%, revisión diaria de patrones repetidos y respuestas NS
          anómalas.
        </p>
      </div>

      <div className="row row--wrap" style={{ marginBottom: 18 }}>
        {tabs
          .filter((t) => t.visible)
          .map((t) => (
            <button
              key={t.id}
              type="button"
              className={`btn btn--sm ${tab === t.id ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
      </div>

      {tab === 'alertas' ? <FlagsTab canResolve={can('quality:verify')} /> : null}
      {tab === 'verificacion' ? <VerificationTab canRead={can('pii:read')} /> : null}
      {tab === 'anulaciones' ? (
        <AnnulmentsTab canDecide={can('interviews:annul')} currentUserId={operator?.id ?? null} />
      ) : null}
      {tab === 'datos' ? <PiiTab canPurge={can('pii:purge')} /> : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                          Alertas automáticas                               */
/* -------------------------------------------------------------------------- */

function FlagsTab({ canResolve }: { canResolve: boolean }) {
  const queryClient = useQueryClient();
  const [severity, setSeverity] = useState('');
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolution, setResolution] = useState('');

  const summary = useQuery({ queryKey: ['quality', 'summary'], queryFn: api.flagSummary });
  const flags = useQuery({
    queryKey: ['quality', 'flags', severity],
    queryFn: () => api.flags({ unresolvedOnly: 'true', ...(severity ? { severity } : {}) }),
  });

  const resolve = useMutation({
    mutationFn: ({ flagId, text }: { flagId: string; text: string }) =>
      api.resolveFlag(flagId, text),
    onSuccess: () => {
      setResolvingId(null);
      setResolution('');
      void queryClient.invalidateQueries({ queryKey: ['quality'] });
    },
  });

  return (
    <>
      {summary.data && summary.data.length > 0 ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">Alertas por tipo</h2>
              <p className="panel__note">
                Una alerta manda la entrevista a auditoría; no la anula. §9.4 dice que las
                entrevistas de menos de 10 minutos «se auditan y, en su caso, se anulan».
              </p>
            </div>
          </div>

          <BarChart
            rows={summary.data.map((flag) => ({
              key: flag.flag,
              label: flag.label,
              value: flag.open,
              valueLabel: `${fmtInt(flag.open)} / ${fmtInt(flag.total)}`,
              status:
                flag.severity === 'critical'
                  ? ('critical' as const)
                  : flag.severity === 'warning'
                    ? ('warning' as const)
                    : undefined,
              tooltip: `${flag.label}: ${fmtInt(flag.open)} sin resolver de ${fmtInt(flag.total)} · severidad ${flag.severity}`,
            }))}
          />
        </div>
      ) : null}

      <div className="filters">
        <div className="field">
          <label className="field__label" htmlFor="severity">
            Severidad
          </label>
          <select
            id="severity"
            className="select"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            <option value="">Todas</option>
            <option value="critical">Crítica</option>
            <option value="warning">Advertencia</option>
            <option value="info">Informativa</option>
          </select>
        </div>
      </div>

      {flags.data ? (
        flags.data.items.length === 0 ? (
          <div className="callout callout--good">
            <span className="callout__title">
              <StatusChip status="good">Sin alertas pendientes</StatusChip>
            </span>
            No hay banderas sin resolver con el filtro actual.
          </div>
        ) : (
          <div className="panel">
            <div className="panel__head">
              <h2 className="panel__title">
                {fmtInt(flags.data.total)} alerta(s) sin resolver
              </h2>
            </div>

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Alerta</th>
                    <th>Entrevista</th>
                    <th>Encuestador</th>
                    <th>Detalle</th>
                    {canResolve ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {flags.data.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <StatusChip
                          status={
                            item.severity === 'critical'
                              ? 'critical'
                              : item.severity === 'warning'
                                ? 'warning'
                                : 'neutral'
                          }
                        >
                          {item.label}
                        </StatusChip>
                        <div className="faint" style={{ marginTop: 4 }}>
                          {new Date(item.createdAt).toLocaleString('es-MX', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </td>
                      <td>
                        <div className="mono faint">{item.interview.clientUuid.slice(0, 8)}</div>
                        <div className="faint">
                          Congl. {item.interview.cluster.id} · sec.{' '}
                          {item.interview.cluster.seccion}
                        </div>
                        {item.interview.durationMinutes !== null ? (
                          <div className="faint num">
                            {fmtDecimal(item.interview.durationMinutes)} min
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div className="strong">{item.interview.encuestador.code}</div>
                        <div className="faint">{item.interview.encuestador.name}</div>
                      </td>
                      <td style={{ maxWidth: 360 }}>
                        <FlagDetail item={item} />
                      </td>
                      {canResolve ? (
                        <td>
                          {resolvingId === item.id ? (
                            <div className="stack" style={{ minWidth: 220 }}>
                              <input
                                className="input"
                                placeholder="Cómo se resolvió"
                                value={resolution}
                                onChange={(e) => setResolution(e.target.value)}
                              />
                              <div className="row">
                                <button
                                  type="button"
                                  className="btn btn--primary btn--sm"
                                  disabled={resolution.trim().length < 3 || resolve.isPending}
                                  onClick={() =>
                                    resolve.mutate({ flagId: item.id, text: resolution.trim() })
                                  }
                                >
                                  Guardar
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--sm"
                                  onClick={() => {
                                    setResolvingId(null);
                                    setResolution('');
                                  }}
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="btn btn--secondary btn--sm"
                              onClick={() => setResolvingId(item.id)}
                            >
                              Resolver
                            </button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="spinner" />
      )}
    </>
  );
}

/** Muestra el detalle de la bandera en los términos de cada control. */
function FlagDetail({
  item,
}: {
  item: {
    flag: string;
    detail: Record<string, unknown> | null;
    interview: {
      gpsStatus: string | null;
      distanceToExpectedMeters: number | null;
      outOfPolygonReason: string | null;
      outOfPolygonNote: string | null;
      expectedFrameSeccion: number | null;
      detectedFrameSeccion: number | null;
    };
  };
}) {
  const { interview, detail } = item;

  if (item.flag === 'gps-outside-polygon' || item.flag === 'section-mismatch') {
    return (
      <>
        <div>
          Esperada <span className="strong num">{interview.expectedFrameSeccion ?? '?'}</span> ·
          detectada <span className="strong num">{interview.detectedFrameSeccion ?? 'ninguna'}</span>
        </div>
        {interview.distanceToExpectedMeters !== null ? (
          <div className="faint num">
            A {Math.round(interview.distanceToExpectedMeters)} m del polígono
          </div>
        ) : null}
        {interview.outOfPolygonReason ? (
          <div className="faint">
            Motivo del encuestador: {interview.outOfPolygonReason}
            {interview.outOfPolygonNote ? ` · ${interview.outOfPolygonNote}` : ''}
          </div>
        ) : null}
      </>
    );
  }

  if (item.flag === 'duration-below-minimum' && detail) {
    return (
      <>
        <div>
          Duración <span className="strong num">{String(detail['durationMinutes'])} min</span> ·
          mínimo {String(detail['threshold'])} min
        </div>
        <div className="faint">{String(detail['note'] ?? '')}</div>
      </>
    );
  }

  if (item.flag === 'straight-lining' && detail) {
    return (
      <div>
        Patrón repetido en: <span className="mono">{(detail['questions'] as string[]).join(', ')}</span>
      </div>
    );
  }

  if (item.flag === 'anomalous-non-substantive' && detail) {
    return (
      <div>
        {fmtPct(Number(detail['ratio']), 1)} de respuestas NS ({String(detail['nonSubstantive'])} de{' '}
        {String(detail['applied'])})
      </div>
    );
  }

  return (
    <span className="faint mono">
      {detail ? JSON.stringify(detail) : '—'}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Verificación                                   */
/* -------------------------------------------------------------------------- */

function VerificationTab({ canRead }: { canRead: boolean }) {
  const queryClient = useQueryClient();
  const [openScript, setOpenScript] = useState<string | null>(null);

  const progress = useQuery({
    queryKey: ['quality', 'verification', 'progress'],
    queryFn: api.verificationProgress,
  });
  const queue = useQuery({
    queryKey: ['quality', 'verification', 'queue'],
    queryFn: () => api.verificationQueue({ pendingOnly: 'true' }),
  });

  const draw = useMutation({
    mutationFn: (mode: 'telefonica' | 'presencial') => api.drawVerification(mode),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['quality', 'verification'] }),
  });

  return (
    <>
      {progress.data ? (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">
                Avance de la supervisión <span className="ref">§9.4</span>
              </h2>
              <p className="panel__note">
                Sobre {fmtInt(progress.data.eligibleInterviews)} entrevistas completas elegibles. La
                selección es aleatoria estratificada por encuestador: una muestra aleatoria simple
                podría no tocar nunca a quien lleva pocas entrevistas, que es justo el caso que la
                supervisión busca detectar.
              </p>
            </div>
          </div>

          <div className="grid grid--2">
            {progress.data.byMode.map((mode) => (
              <div key={mode.mode}>
                <div className="row row--between" style={{ marginBottom: 8 }}>
                  <span className="strong" style={{ textTransform: 'capitalize' }}>
                    {mode.mode} · meta {fmtPct(mode.targetShare, 0)}
                  </span>
                  <span className="muted num">
                    {fmtInt(mode.completed)} / {fmtInt(mode.targetCount)}
                  </span>
                </div>
                <div className="hbar__track">
                  <span
                    className="hbar__mark"
                    style={{
                      width: `${Math.min(100, mode.targetCount > 0 ? (mode.completed / mode.targetCount) * 100 : 0)}%`,
                    }}
                  />
                </div>
                <div className="row row--wrap" style={{ marginTop: 10 }}>
                  {mode.pending > 0 ? (
                    <StatusChip status="warning">{mode.pending} en cola</StatusChip>
                  ) : null}
                  {mode.outcomes.map((outcome) => (
                    <StatusChip
                      key={outcome.outcome}
                      status={
                        outcome.outcome === 'confirmada'
                          ? 'good'
                          : outcome.outcome === 'falsa' || outcome.outcome === 'inconsistente'
                            ? 'critical'
                            : 'neutral'
                      }
                    >
                      {outcome.outcome}: {outcome.count}
                    </StatusChip>
                  ))}
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => draw.mutate(mode.mode as 'telefonica' | 'presencial')}
                    disabled={draw.isPending}
                  >
                    Sortear muestra
                  </button>
                </div>
              </div>
            ))}
          </div>

          {draw.data ? (
            <div className="callout callout--good" style={{ marginTop: 16, marginBottom: 0 }}>
              Se seleccionaron {draw.data.selected} entrevistas de {draw.data.eligible} elegibles.
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">Cola de verificación</h2>
            <p className="panel__note">
              La llamada se hace entre 24 y 48 h después de la entrevista. «No contactada» deja la
              verificación abierta para reintentar: cerrarla al primer intento inflaría la
              supervisión cumplida sin haber verificado nada.
            </p>
          </div>
        </div>

        {queue.data ? (
          queue.data.length === 0 ? (
            <p className="muted">No hay verificaciones pendientes. Sortea una muestra para empezar.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Modo</th>
                    <th>Entrevista</th>
                    <th>Contacto</th>
                    <th>Ventana</th>
                    <th>Intentos</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {queue.data.map((item) => (
                    <>
                      <tr key={item.id}>
                        <td>
                          <StatusChip status={item.overdue ? 'warning' : 'neutral'}>
                            {item.mode}
                          </StatusChip>
                        </td>
                        <td>
                          <div className="faint">
                            Congl. {item.interview.cluster.id} · sec. {item.interview.cluster.seccion}
                          </div>
                          <div className="faint">
                            {item.interview.encuestador.code} ·{' '}
                            {item.interview.durationMinutes !== null
                              ? `${fmtDecimal(item.interview.durationMinutes)} min`
                              : '—'}
                          </div>
                          {item.interview.qualityFlags.length > 0 ? (
                            <StatusChip status="warning">
                              {item.interview.qualityFlags.length} alerta(s)
                            </StatusChip>
                          ) : null}
                        </td>
                        <td>
                          {item.interview.contactPurged ? (
                            <span className="faint">purgado</span>
                          ) : (
                            <>
                              <span className="mono">{item.interview.phoneMasked}</span>
                              {canRead ? (
                                <RevealContact interviewId={item.interview.id} />
                              ) : null}
                            </>
                          )}
                        </td>
                        <td className="faint">
                          {item.dueFrom && item.dueTo
                            ? `${new Date(item.dueFrom).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit' })} – ${new Date(item.dueTo).toLocaleString('es-MX', { hour: '2-digit' })}`
                            : '—'}
                          {item.overdue ? (
                            <div>
                              <StatusChip status="warning">Fuera de ventana</StatusChip>
                            </div>
                          ) : null}
                        </td>
                        <td className="num">{item.attempts}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() => setOpenScript(openScript === item.id ? null : item.id)}
                          >
                            {openScript === item.id ? 'Cerrar' : 'Guion'}
                          </button>
                        </td>
                      </tr>
                      {openScript === item.id ? (
                        <tr key={`${item.id}-script`}>
                          <td colSpan={6} style={{ background: 'var(--surface-alt)' }}>
                            <VerificationScript
                              verificationId={item.id}
                              onDone={() => {
                                setOpenScript(null);
                                void queryClient.invalidateQueries({
                                  queryKey: ['quality', 'verification'],
                                });
                              }}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="spinner" />
        )}
      </div>
    </>
  );
}

/**
 * Revela el contacto descifrado.
 *
 * Cada consulta queda en la bitácora: §11 limita el uso de estos datos al
 * control de calidad, y registrar quién los consultó es lo que hace verificable
 * ese límite. Por eso no se descifran al cargar la tabla, sino bajo demanda.
 */
function RevealContact({ interviewId }: { interviewId: string }) {
  const [revealed, setRevealed] = useState<{ name: string | null; phone: string | null } | null>(
    null,
  );
  const reveal = useMutation({
    mutationFn: () => api.contact(interviewId),
    onSuccess: (data) => setRevealed({ name: data.name, phone: data.phone }),
  });

  if (revealed) {
    return (
      <div className="faint">
        {revealed.name ?? 'sin nombre'} · <span className="mono">{revealed.phone ?? '—'}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="btn btn--ghost btn--sm"
      style={{ paddingLeft: 0 }}
      onClick={() => reveal.mutate()}
      disabled={reveal.isPending}
    >
      Ver teléfono
    </button>
  );
}

/**
 * Guion de la llamada con las 5 respuestas clave de §9.4.
 *
 * El supervisor confirma punto por punto. Marcar cada respuesta como coincidente
 * o no es lo que convierte la verificación en evidencia: un «confirmada» sin
 * detalle no distingue una entrevista real de una inventada.
 */
function VerificationScript({
  verificationId,
  onDone,
}: {
  verificationId: string;
  onDone: () => void;
}) {
  const script = useQuery({
    queryKey: ['quality', 'verification', 'script', verificationId],
    queryFn: () => api.verificationScript(verificationId),
  });
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [note, setNote] = useState('');

  const complete = useMutation({
    mutationFn: (outcome: string) =>
      api.completeVerification(verificationId, {
        outcome,
        keyAnswerChecks: checks,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: onDone,
  });

  if (!script.data) return <div className="spinner" />;

  const matched = Object.values(checks).filter(Boolean).length;
  const total = script.data.keyAnswers.length;

  return (
    <div style={{ padding: '14px 0' }}>
      <p className="field__label">
        Confirma la entrevista y estas {total} respuestas clave
      </p>

      <div className="stack">
        {script.data.keyAnswers.map((answer) => (
          <div
            key={answer.questionCode}
            className="row row--between"
            style={{
              padding: '10px 12px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              gap: 14,
            }}
          >
            <div style={{ flex: 1 }}>
              <div className="faint mono">{answer.questionCode}</div>
              <div style={{ fontSize: 14 }}>{answer.questionText}</div>
              <div className="strong" style={{ marginTop: 4 }}>
                Se capturó:{' '}
                {answer.capturedAnswers
                  .map(
                    (a) =>
                      a.optionLabel ??
                      (a.numericValue !== null ? String(a.numericValue) : (a.textValue ?? '—')),
                  )
                  .join(' · ')}
              </div>
            </div>
            <label className="row" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={checks[answer.questionCode] ?? false}
                onChange={(e) =>
                  setChecks((prev) => ({ ...prev, [answer.questionCode]: e.target.checked }))
                }
              />
              Coincide
            </label>
          </div>
        ))}
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label className="field__label" htmlFor={`note-${verificationId}`}>
          Observaciones de la llamada
        </label>
        <textarea
          id={`note-${verificationId}`}
          className="textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div className="row row--wrap">
        <span className="muted">
          {matched} de {total} coinciden
        </span>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => complete.mutate('confirmada')}
          disabled={complete.isPending}
        >
          Confirmada
        </button>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => complete.mutate('no_contactada')}
          disabled={complete.isPending}
        >
          No contestó (reintentar)
        </button>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => complete.mutate('rechazo_verificacion')}
          disabled={complete.isPending}
        >
          No quiso verificar
        </button>
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={() => complete.mutate('inconsistente')}
          disabled={complete.isPending}
        >
          Inconsistente
        </button>
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={() => complete.mutate('falsa')}
          disabled={complete.isPending}
        >
          Falsa
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Anulaciones                                   */
/* -------------------------------------------------------------------------- */

function AnnulmentsTab({
  canDecide,
  currentUserId,
}: {
  canDecide: boolean;
  currentUserId: string | null;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const annulments = useQuery({
    queryKey: ['quality', 'annulments'],
    queryFn: () => api.annulments(),
  });
  const overview = useQuery({
    queryKey: ['monitoring', 'overview'],
    queryFn: api.monitoringOverview,
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api.decideAnnulment(id, approve),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['quality'] });
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo decidir.'),
  });

  const pending = annulments.data?.filter((a) => a.status === 'solicitada') ?? [];

  return (
    <>
      <div className="grid grid--3">
        <Tile
          label="Solicitudes pendientes"
          value={fmtInt(pending.length)}
          meta="Requieren un segundo usuario"
        />
        <Tile
          label="Anuladas"
          value={fmtInt(overview.data?.counts.annulled ?? 0)}
          meta={
            overview.data
              ? `${fmtPct(overview.data.counts.annulmentRate, 1)} de las levantadas`
              : undefined
          }
        />
        <Tile
          label="Merma presupuestada"
          reference="§2"
          value={fmtPct(overview.data?.counts.expectedAnnulmentRate ?? 0.1, 0)}
          meta="880 programadas para 800 efectivas"
        />
      </div>

      <div className="callout callout--info">
        <span className="callout__title">Por qué hacen falta dos personas</span>
        La anulación requiere la aprobación de un usuario distinto de quien la solicita. Que una
        sola persona pueda depurar la base a su criterio es exactamente el riesgo contra el que §11
        recomienda que la validación de datos la haga un tercero. La entrevista anulada no se borra:
        se marca y queda fuera de los conteos, porque §11 pide conservar la base cruda para
        auditoría.
      </div>

      {error ? <div className="callout callout--critical">{error}</div> : null}

      {annulments.data && annulments.data.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Entrevista</th>
                <th>Motivo</th>
                <th>Solicitó</th>
                <th>Estado</th>
                {canDecide ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {annulments.data.map((item) => {
                const isOwnRequest = item.requestedBy?.id === currentUserId;
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="faint">
                        Congl. {item.interview.cluster.id} · sec. {item.interview.cluster.seccion}
                      </div>
                      <div className="faint">
                        {item.interview.encuestador.code}
                        {item.interview.durationMinutes !== null
                          ? ` · ${fmtDecimal(item.interview.durationMinutes)} min`
                          : ''}
                      </div>
                      {item.interview.qualityFlags.length > 0 ? (
                        <div className="faint">{item.interview.qualityFlags.join(', ')}</div>
                      ) : null}
                    </td>
                    <td style={{ maxWidth: 320 }}>
                      {item.reason}
                      {item.note ? <div className="faint">{item.note}</div> : null}
                    </td>
                    <td className="faint">
                      {item.requestedBy?.name ?? '—'}
                      <div>
                        {new Date(item.requestedAt).toLocaleString('es-MX', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </td>
                    <td>
                      <StatusChip
                        status={
                          item.status === 'aprobada'
                            ? 'critical'
                            : item.status === 'rechazada'
                              ? 'neutral'
                              : 'warning'
                        }
                      >
                        {item.status}
                      </StatusChip>
                      {item.approvedBy ? (
                        <div className="faint">por {item.approvedBy.name}</div>
                      ) : null}
                    </td>
                    {canDecide ? (
                      <td>
                        {item.status === 'solicitada' ? (
                          isOwnRequest ? (
                            <span className="faint">
                              La solicitaste tú; debe aprobarla otra persona
                            </span>
                          ) : (
                            <div className="row">
                              <button
                                type="button"
                                className="btn btn--danger btn--sm"
                                onClick={() => decide.mutate({ id: item.id, approve: true })}
                                disabled={decide.isPending}
                              >
                                Anular
                              </button>
                              <button
                                type="button"
                                className="btn btn--secondary btn--sm"
                                onClick={() => decide.mutate({ id: item.id, approve: false })}
                                disabled={decide.isPending}
                              >
                                Rechazar
                              </button>
                            </div>
                          )
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No hay solicitudes de anulación.</p>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                          Datos de contacto                                 */
/* -------------------------------------------------------------------------- */

function PiiTab({ canPurge }: { canPurge: boolean }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [force, setForce] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({ queryKey: ['quality', 'pii', 'status'], queryFn: api.piiStatus });

  const purge = useMutation({
    mutationFn: () => api.purgePii(reason.trim(), force),
    onSuccess: (data) => {
      setResult(`Se purgaron ${fmtInt(data.purged)} registros de contacto.`);
      setError(null);
      setReason('');
      setConfirmText('');
      void queryClient.invalidateQueries({ queryKey: ['quality', 'pii'] });
    },
    onError: (e) => {
      setResult(null);
      setError(e instanceof ApiError ? e.message : 'No se pudo purgar.');
    },
  });

  return (
    <>
      <div className="callout callout--info">
        <span className="callout__title">Qué son estos datos</span>
        Q1 (nombre de pila) y Q2 (teléfono) del Bloque 12, ambos opcionales. §11 los limita al
        control de calidad y obliga a eliminarlos tras la auditoría. Se guardan cifrados con
        AES-256-GCM en tabla aparte, no aparecen en ninguna exportación de análisis, y cada consulta
        queda en la bitácora.
      </div>

      {status.data ? (
        <>
          <div className="grid grid--3">
            <Tile label="Contactos vigentes" value={fmtInt(status.data.withContact)} />
            <Tile label="Ya purgados" value={fmtInt(status.data.purged)} />
            <Tile
              label="Verificaciones telefónicas abiertas"
              value={fmtInt(status.data.pendingPhoneVerifications)}
            />
          </div>

          {status.data.blockingReason ? (
            <div className="callout callout--warning">
              <span className="callout__title">
                <StatusChip status="warning">Aún no es seguro purgar</StatusChip>
              </span>
              {status.data.blockingReason}
            </div>
          ) : status.data.withContact > 0 ? (
            <div className="callout callout--good">
              <span className="callout__title">
                <StatusChip status="good">Se puede cerrar la auditoría</StatusChip>
              </span>
              No quedan verificaciones telefónicas abiertas.
            </div>
          ) : null}

          {canPurge ? (
            <div className="panel">
              <div className="panel__head">
                <div>
                  <h2 className="panel__title">Purgar datos de contacto</h2>
                  <p className="panel__note">
                    Los campos se sobrescriben con nulo en lugar de borrar la fila, para dejar
                    constancia auditable de que el dato existió y de que se eliminó, con quién y
                    cuándo. La operación no se puede revertir.
                  </p>
                </div>
              </div>

              <div className="field">
                <label className="field__label" htmlFor="purge-reason">
                  Motivo del cierre de auditoría
                </label>
                <input
                  id="purge-reason"
                  className="input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="p. ej. Auditoría de la oleada W1 cerrada el 30 de noviembre"
                />
              </div>

              {!status.data.safeToPurge && status.data.withContact > 0 ? (
                <label className="row" style={{ fontSize: 14, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={force}
                    onChange={(e) => setForce(e.target.checked)}
                  />
                  Purgar de todos modos, aunque queden verificaciones abiertas
                </label>
              ) : null}

              <div className="field">
                <label className="field__label" htmlFor="purge-confirm">
                  Escribe <span className="mono strong">PURGAR</span> para confirmar
                </label>
                <input
                  id="purge-confirm"
                  className="input"
                  style={{ maxWidth: 200 }}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  autoCapitalize="characters"
                />
              </div>

              <button
                type="button"
                className="btn btn--danger"
                disabled={
                  reason.trim().length < 5 ||
                  confirmText.trim().toUpperCase() !== 'PURGAR' ||
                  status.data.withContact === 0 ||
                  purge.isPending
                }
                onClick={() => purge.mutate()}
              >
                {purge.isPending ? 'Purgando…' : 'Purgar definitivamente'}
              </button>

              {result ? (
                <div className="callout callout--good" style={{ marginTop: 14, marginBottom: 0 }}>
                  {result}
                </div>
              ) : null}
              {error ? (
                <div className="callout callout--critical" style={{ marginTop: 14, marginBottom: 0 }}>
                  {error}
                </div>
              ) : null}
            </div>
          ) : (
            <p className="muted">
              Sólo el superadministrador puede purgar los datos de contacto.
            </p>
          )}
        </>
      ) : (
        <div className="spinner" />
      )}
    </>
  );
}
